import { useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import Modal from '@mui/material/Modal'

import { useAppSelector } from 'app/hooks'
import { formatWithCommasOrExponential } from 'common/utils/chart/chartUtils'

import { selectBaseSettings } from 'features/Settings/settingsSelectors'

export interface IConfigDetailModal {
  isModalOpen: boolean
  closeModal: () => void
}

export interface IActionBarProps {
  closeModal: () => void
}

interface IHMCAConfigInfo {
  MethodName: string
  ClassLocationClusteringDistance: string
  HMCAStepDistance: string
  HMCAMinNCALength: string
  ClassLocationUnitLength: string
  ClassLocationUnitWidth: string
  ClassLocationStepDistance: string
  ClassLocationClass4Trigger: string
  ClassLocationMinClass1Length: string
}

const defaultConfigInfo: IHMCAConfigInfo = {
  MethodName: 'Unavailable',
  ClassLocationClusteringDistance: '',
  HMCAStepDistance: '',
  HMCAMinNCALength: '',
  ClassLocationUnitLength: '',
  ClassLocationUnitWidth: '',
  ClassLocationStepDistance: '',
  ClassLocationClass4Trigger: '',
  ClassLocationMinClass1Length: ''
}

const ConfigDetailModal = ({ closeModal, isModalOpen }: IConfigDetailModal) => {
  const baseSettings = useAppSelector(selectBaseSettings)

  const [hmcaConfigInfo, setHmcaConfigInfo] =
    useState<IHMCAConfigInfo>(defaultConfigInfo)

  const lookupTables = baseSettings.find(
    (e) => e.groupName === 'HCAMCAConfigDetails'
  )

  const handleClose = (_event: React.SyntheticEvent, reason: string) => {
    if (reason === 'backdropClick') {
      return
    }

    closeModal()
  }

  useEffect(() => {
    if (lookupTables?.details) {
      setHmcaConfigInfo(
        lookupTables.details.reduce(
          (obj, i) => ({ ...obj, [i.key]: i.value }),
          {}
        ) as IHMCAConfigInfo
      )
    }
  }, [baseSettings, lookupTables?.details])

  return (
    <Modal
      open={isModalOpen}
      onClose={handleClose}
      className='flex items-center justify-center'
    >
      <div className='bg-white'>
        <div className='flex border-b-2 border-gray-200 p-4'>
          <div className='font-bold'>Config Details</div>
          <div className='flex-1' />
          <button onClick={closeModal} aria-label='Close'>
            <CloseIcon fontSize='small' />
          </button>
        </div>
        <div className='px-5'>
          <div className='h-full'>
            <div className='py-6 font-semibold text-gray-700 uppercase'>
              Consequence Area
            </div>
            <div className='grid grid-cols-2 pb-6'>
              <div className='w-56 font-semibold'>Method</div>
              <div>{hmcaConfigInfo.MethodName}</div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Step Distance</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.HMCAStepDistance,
                  { forceNormal: true }
                )}{' '}
                ft
              </div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Minimum NCA Length</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.HMCAMinNCALength,
                  { forceNormal: true }
                )}
              </div>
            </div>
            <div className='py-6 font-semibold text-gray-700 uppercase'>
              Class Location
            </div>
            <div className='grid grid-cols-2 pb-12'>
              <div className='w-56 font-semibold'>
                Class Location Unit Length
              </div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.ClassLocationUnitLength,
                  { forceNormal: true }
                )}{' '}
                mi
              </div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Class Location Unit Width</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.ClassLocationUnitWidth,
                  { forceNormal: true }
                )}{' '}
                ft
              </div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Clustering Distance</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.ClassLocationClusteringDistance,
                  { forceNormal: true }
                )}{' '}
                ft
              </div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Step Distance</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.ClassLocationStepDistance,
                  { forceNormal: true }
                )}{' '}
                ft
              </div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Class 4 Trigger</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.ClassLocationClass4Trigger,
                  { forceNormal: true }
                )}
              </div>
              <hr className='col-span-2 my-2 border-gray-300' />
              <div className='font-semibold'>Minimum Class 1 Length</div>
              <div>
                {formatWithCommasOrExponential(
                  hmcaConfigInfo.ClassLocationMinClass1Length,
                  { forceNormal: true }
                )}
              </div>
            </div>
          </div>
        </div>
        <ActionBar closeModal={closeModal} />
      </div>
    </Modal>
  )
}

const ActionBar = ({ closeModal }: IActionBarProps) => {
  return (
    <div
      className={[
        'flex justify-end',
        'p-3',
        'border-t-2 border-gray-200'
      ].join(' ')}
    >
      <button
        className={[
          'w-24 py-2',
          'rounded border border-lighthouseBlue',
          'text-lighthouseBlue'
        ].join(' ')}
        onClick={closeModal}
      >
        DONE
      </button>
    </div>
  )
}

export default ConfigDetailModal
